import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/index';
import { X, User, Mail, Shield, Calendar, Lock, Check, AlertCircle } from 'lucide-react';

export default function ProfileModal({ onClose, campSlug }) {
  const { user, refreshUser } = useAuth();
  const t = useT();
  const overlayRef = useRef(null);

  const [displayName, setDisplayName] = useState(user?.display_name || user?.username || '');
  const [email, setEmail]             = useState(user?.email || '');
  const [infoSaving, setInfoSaving]   = useState(false);
  const [infoMsg, setInfoMsg]         = useState(null); // { ok, text }

  const [pwForm, setPwForm]     = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg]       = useState(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Close on backdrop click
  const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose(); };

  const roles = Array.isArray(user?.roles) ? user.roles : [user?.role || 'viewer'];
  const primaryRole = roles[0] || 'viewer';
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString()
    : null;

  const saveInfo = async (e) => {
    e.preventDefault();
    setInfoSaving(true);
    setInfoMsg(null);
    try {
      await axios.put(`/api/${campSlug}/auth/me`, { display_name: displayName, email });
      await refreshUser();
      setInfoMsg({ ok: true, text: t('profile.saved') });
    } catch (err) {
      setInfoMsg({ ok: false, text: err.response?.data?.error || t('common.error') });
    } finally {
      setInfoSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      setPwMsg({ ok: false, text: t('profile.password_mismatch') });
      return;
    }
    if (pwForm.next.length < 8) {
      setPwMsg({ ok: false, text: t('profile.password_min') });
      return;
    }
    setPwSaving(true);
    setPwMsg(null);
    try {
      await axios.post(`/api/${campSlug}/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      setPwMsg({ ok: true, text: t('profile.updated') });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwMsg({ ok: false, text: err.response?.data?.error || t('common.error') });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="profile-modal-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="profile-modal">
        {/* Header */}
        <div className="profile-modal-header">
          <div className="profile-modal-avatar">
            {(user?.display_name || user?.username || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="profile-modal-header-info">
            <h2 className="profile-modal-title">{t('profile.title')}</h2>
            <span className="profile-modal-username">@{user?.username}</span>
          </div>
          <button className="profile-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="profile-modal-body">
          {/* ── Account info form ── */}
          <section className="profile-section">
            <h3 className="profile-section-title">
              <User size={14} />
              {t('profile.account_info')}
            </h3>

            <div className="profile-info-grid">
              <div className="profile-info-item">
                <User size={13} className="profile-info-icon" />
                <div>
                  <span className="profile-info-label">{t('profile.username')}</span>
                  <span className="profile-info-value">{user?.username}</span>
                </div>
              </div>
              <div className="profile-info-item">
                <Shield size={13} className="profile-info-icon" />
                <div>
                  <span className="profile-info-label">{t('profile.role')}</span>
                  <span className="profile-info-value">{t('role.' + primaryRole)}</span>
                </div>
              </div>
              {memberSince && (
                <div className="profile-info-item">
                  <Calendar size={13} className="profile-info-icon" />
                  <div>
                    <span className="profile-info-label">{t('profile.member_since')}</span>
                    <span className="profile-info-value">{memberSince}</span>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={saveInfo} className="profile-form">
              <div className="form-group">
                <label className="form-label">{t('profile.display_name')}</label>
                <input
                  className="form-input"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <Mail size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                  {t('profile.email')}
                </label>
                <input
                  className="form-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>

              {infoMsg && (
                <div className={`profile-msg${infoMsg.ok ? ' profile-msg--ok' : ' profile-msg--err'}`}>
                  {infoMsg.ok ? <Check size={14} /> : <AlertCircle size={14} />}
                  {infoMsg.text}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={infoSaving}>
                  {infoSaving ? t('profile.saving') : t('profile.save')}
                </button>
              </div>
            </form>
          </section>

          <div className="profile-section-divider" />

          {/* ── Password change ── */}
          <section className="profile-section">
            <h3 className="profile-section-title">
              <Lock size={14} />
              {t('profile.change_password')}
            </h3>

            <form onSubmit={savePassword} className="profile-form">
              <div className="form-group">
                <label className="form-label">{t('profile.current_password')}</label>
                <input
                  className="form-input"
                  type="password"
                  value={pwForm.current}
                  onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div className="profile-pw-row">
                <div className="form-group">
                  <label className="form-label">{t('profile.new_password')}</label>
                  <input
                    className="form-input"
                    type="password"
                    value={pwForm.next}
                    onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('profile.confirm_password')}</label>
                  <input
                    className="form-input"
                    type="password"
                    value={pwForm.confirm}
                    onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {pwMsg && (
                <div className={`profile-msg${pwMsg.ok ? ' profile-msg--ok' : ' profile-msg--err'}`}>
                  {pwMsg.ok ? <Check size={14} /> : <AlertCircle size={14} />}
                  {pwMsg.text}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={pwSaving}>
                  {pwSaving ? t('profile.updating') : t('profile.update_password')}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>

      <style>{`
        .profile-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.65);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.15s ease;
        }

        .profile-modal {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-top: 3px solid var(--camp-primary, #e63946);
          border-radius: var(--r-lg);
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 24px 64px rgba(0,0,0,0.5);
          animation: profileModalIn 0.2s cubic-bezier(0.34,1.56,0.64,1);
        }

        @keyframes profileModalIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .profile-modal-header {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 20px 20px 18px;
          border-bottom: 1px solid var(--color-border);
          flex-shrink: 0;
        }

        .profile-modal-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--camp-primary, #e63946);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-display);
          font-size: 1.4rem;
          font-weight: 900;
          flex-shrink: 0;
          border: 2px solid rgba(var(--camp-primary-rgb, 230,57,70), 0.4);
        }

        .profile-modal-header-info { flex: 1; min-width: 0; }
        .profile-modal-title {
          font-family: var(--font-display);
          font-size: 1.1rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--color-text);
          margin: 0 0 2px;
          line-height: 1.2;
        }
        .profile-modal-username {
          font-size: 0.78rem;
          color: var(--color-muted);
        }

        .profile-modal-close {
          width: 32px;
          height: 32px;
          border-radius: var(--r-md);
          background: var(--color-surface-2);
          border: 1px solid var(--color-border);
          color: var(--color-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: var(--t-fast);
        }
        .profile-modal-close:hover { color: var(--color-text); background: var(--color-surface-3, var(--color-surface-2)); }

        .profile-modal-body {
          overflow-y: auto;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 0;
          -webkit-overflow-scrolling: touch;
        }

        .profile-section { display: flex; flex-direction: column; gap: 16px; }
        .profile-section-divider {
          height: 1px;
          background: var(--color-border);
          margin: 22px 0;
        }

        .profile-section-title {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--camp-primary, #e63946);
          margin: 0;
        }

        .profile-info-grid {
          display: flex;
          flex-direction: column;
          gap: 8px;
          background: var(--color-surface-2);
          border: 1px solid var(--color-border);
          border-radius: var(--r-md);
          padding: 14px 16px;
        }

        .profile-info-item {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .profile-info-icon { color: var(--color-muted); flex-shrink: 0; }
        .profile-info-label {
          display: block;
          font-size: 0.68rem;
          color: var(--color-muted);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 1px;
        }
        .profile-info-value {
          display: block;
          font-size: 0.88rem;
          font-weight: 600;
          color: var(--color-text);
        }

        .profile-form {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .profile-form .form-group { margin: 0; }

        .profile-pw-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 480px) {
          .profile-pw-row { grid-template-columns: 1fr; }
        }

        .profile-msg {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 13px;
          border-radius: var(--r-md);
          font-size: 0.84rem;
          font-weight: 500;
        }
        .profile-msg--ok {
          background: rgba(16,185,129,0.1);
          border: 1px solid rgba(16,185,129,0.3);
          color: #10b981;
        }
        .profile-msg--err {
          background: rgba(239,68,68,0.1);
          border: 1px solid rgba(239,68,68,0.3);
          color: #ef4444;
        }
      `}</style>
    </div>
  );
}
